import React, { useState } from 'react';
import { Badge } from 'src/components/ui/badge';
import { Card } from 'src/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from 'src/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { STATIC_USERS, STATIC_CURRENT_USER } from './staticData';
import { toast } from 'sonner';
import { UserPlus, KeyRound, Edit3, Trash2, MailCheck, MoreVertical } from 'lucide-react';
import { showFreeAssessmentToast } from '../../utils/freeAssessmentRedirect';

const RoleBadge = ({ role }) => {
  const color = role === 'root' ? 'bg-purple-100 text-purple-800' : role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800';
  return <Badge className={`${color} capitalize`}>{role}</Badge>;
};


export default function UsersSettings() {
  const [me] = useState(STATIC_CURRENT_USER);
  const [users, setUsers] = useState(STATIC_USERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [createForm, setCreateForm] = useState({ email: '', password: '', fullname: '', position: '', role: 'member' });
  const [editForm, setEditForm] = useState({ user_id: '', fullname: '', position: '' });
  const [passwordForm, setPasswordForm] = useState({ user_id: '', new_password: '' });
  const [ownPasswordForm, setOwnPasswordForm] = useState({ current_password: '', new_password: '' });
  const [forgotForm, setForgotForm] = useState({ email: '', backup_code: '', new_password: '' });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });

  const canManage = me?.role === 'root';

  // Password validation helper used for create user form
  const validatePassword = (pw = "") => {
    const length = typeof pw === 'string' && pw.length >= 16;
    const uppercase = /[A-Z]/.test(pw);
    const lowercase = /[a-z]/.test(pw);
    const special = /[!@#$%^&]/.test(pw);
    // No more than 3 consecutive repeated characters (i.e., disallow 4+ same chars in a row)
    const noConsecutive = !/(.)\1\1\1/.test(pw);
    return {
      length,
      uppercase,
      lowercase,
      special,
      noConsecutive,
      valid: length && uppercase && lowercase && special && noConsecutive,
    };
  };

  // Derived state for Create button enablement
  const allCreateFieldsFilled = createForm.email.trim() && createForm.password && createForm.fullname.trim();
  const createPasswordValidation = validatePassword(createForm.password);
  const canCreate = !submitting && !!allCreateFieldsFilled && createPasswordValidation.valid;

  return (
    <div className="space-y-6">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-gray-900">Access Control</h2>
          <p className="text-sm text-gray-500">Manage user accounts and permissions</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-[#44087D] hover:bg-[#44087D]/90 text-white"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create User</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Email</Label>
                    <Input value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="admin@example.com" />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Strong#Pass1" />
                    {/* Compact password requirements badges */}
                    {(() => {
                      const v = validatePassword(createForm.password);
                      const items = [
                        { ok: v.length, label: '16+ chars', title: 'At least 16 characters' },
                        { ok: v.uppercase, label: 'Uppercase', title: 'Contains an uppercase letter' },
                        { ok: v.lowercase, label: 'Lowercase', title: 'Contains a lowercase letter' },
                        { ok: v.special, label: 'Special (!@#$%^&)', title: 'Contains a special character' },
                        { ok: v.noConsecutive, label: 'No 4+ repeats', title: 'No more than 3 identical characters in a row' },
                      ];
                      return (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {items.map((it, i) => (
                            <span
                              key={i}
                              title={it.title}
                              className={`text-xs px-2 py-1 rounded-full border ${it.ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-100'}`}
                            >
                              {it.label}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <Label>Fullname</Label>
                    <Input value={createForm.fullname} onChange={(e) => setCreateForm({ ...createForm, fullname: e.target.value })} placeholder="Admin User" />
                  </div>
                  <div>
                    <Label>Position</Label>
                    <Input value={createForm.position} onChange={(e) => setCreateForm({ ...createForm, position: e.target.value })} placeholder="Position" />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                      <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="member">member</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <span
                    title={!canCreate ? 'All fields are required and password validations must be satisfied' : ''}
                    className={!canCreate ? 'inline-block cursor-not-allowed' : 'inline-block'}
                  >
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      disabled={!canCreate}
                      onClick={async () => {
                      try {
                        setSubmitting(true);
                        if (!createForm.email || !createForm.password || !createForm.fullname) {
                          toast.error('Please fill in Email, Password and Fullname.');
                          return;
                        }

                        const pv = validatePassword(createForm.password);
                        if (!pv.valid) {
                          // Show toast explaining unmet requirements
                          const parts = [];
                          if (!pv.length) parts.push('16+ characters');
                          if (!pv.uppercase) parts.push('an uppercase letter');
                          if (!pv.lowercase) parts.push('a lowercase letter');
                          if (!pv.special) parts.push('a special character (!@#$%^&)');
                          if (!pv.noConsecutive) parts.push('no more than 3 identical characters in a row');
                          toast.error('Password must include: ' + parts.join(', '));
                          return;
                        }

                        // Show toast for USP feature
                        showFreeAssessmentToast();
                        setCreateOpen(false);
                        setCreateForm({ email: '', password: '', fullname: '', position: '', role: 'member' });
                      } catch (e) {
                        const msg = e?.response?.data?.message || e?.message || 'Failed to create user';
                        toast.error('Failed to create user — ' + msg);
                      } finally {
                        setSubmitting(false);
                      }
                      }}
                    >
                      Create
                    </Button>
                  </span>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="text-gray-700">
                  <MailCheck className="w-4 h-4 mr-2" />
                  Reset via Backup
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Forgot password (backup code)</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Email</Label>
                    <Input value={forgotForm.email} onChange={(e) => setForgotForm({ ...forgotForm, email: e.target.value })} placeholder="user@example.com" />
                  </div>
                  <div>
                    <Label>Backup code</Label>
                    <Input value={forgotForm.backup_code} onChange={(e) => setForgotForm({ ...forgotForm, backup_code: e.target.value })} placeholder="single-use code" />
                  </div>
                  <div>
                    <Label>New password</Label>
                    <Input type="password" value={forgotForm.new_password} onChange={(e) => setForgotForm({ ...forgotForm, new_password: e.target.value })} placeholder="New#Pass1" />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    disabled={submitting}
                    onClick={async () => {
                      try {
                        setSubmitting(true);
                        if (!forgotForm.email || !forgotForm.backup_code || !forgotForm.new_password) {
                          toast.error('All fields are required');
                          return;
                        }
                        // Show toast for USP feature
                        showFreeAssessmentToast();
                        setForgotOpen(false);
                        setForgotForm({ email: '', backup_code: '', new_password: '' });
                      } catch (e) {
                        toast.error(e?.response?.data?.message || e?.message || 'Failed to reset password');
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    Reset
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <Card className="border shadow-sm">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b">
              <TableHead className="font-medium">Name</TableHead>
              <TableHead className="font-medium">Email</TableHead>
              <TableHead className="font-medium">Role</TableHead>
              <TableHead className="font-medium">Position</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No users found</TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id || u._id || u.email} className="hover:bg-gray-50">
                  <TableCell className="font-medium">{u.fullname || '-'}</TableCell>
                  <TableCell className="text-gray-600">{u.email}</TableCell>
                  <TableCell><RoleBadge role={u.role} /></TableCell>
                  <TableCell className="text-gray-600">{u.position || '-'}</TableCell>
                  <TableCell>
                    {canManage ? (
                      <DropdownMenu >
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(u);
                              setEditForm({ user_id: u.id || u._id, fullname: u.fullname || '', position: u.position || '' });
                              setEditOpen(true);
                            }}
                          >
                            <Edit3 className="w-4 h-4 mr-2" /> Edit Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(u);
                              setPasswordForm({ user_id: u.id || u._id, new_password: '' });
                              setPasswordOpen(true);
                            }}
                          >
                            <KeyRound className="w-4 h-4 mr-2" /> Change Password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() => setDeleteDialog({ open: true, user: u })}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : me?.email === u.email ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(u);
                              setEditForm({ user_id: u.id || u._id, fullname: u.fullname || '', position: u.position || '' });
                              setEditOpen(true);
                            }}
                          >
                            <Edit3 className="w-4 h-4 mr-2" /> Edit Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(u);
                              setOwnPasswordForm({ current_password: '', new_password: '' });
                              setPasswordOpen(true);
                            }}
                          >
                            <KeyRound className="w-4 h-4 mr-2" /> Change Password
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={open => setDeleteDialog(d => ({ ...d, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <span className="font-semibold text-gray-900">{deleteDialog.user?.email}</span>?
            </p>
            <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeleteDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                if (!deleteDialog.user) return;
                try {
                  // Show toast for USP feature
                  showFreeAssessmentToast();
                  setDeleteDialog({ open: false, user: null });
                } catch (e) {
                  toast.error(e?.response?.data?.message || e?.message || 'Failed to delete user');
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Fullname</Label>
              <Input value={editForm.fullname} onChange={(e) => setEditForm({ ...editForm, fullname: e.target.value })} />
            </div>
            <div>
              <Label>Position</Label>
              <Input value={editForm.position} onChange={(e) => setEditForm({ ...editForm, position: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button
              size="sm"
              className="bg-[#44087D] hover:bg-[#44087D]/90 text-white"
              disabled={submitting}
              onClick={async () => {
                try {
                  setSubmitting(true);
                  if (!editForm.fullname) {
                    toast.error('Fullname is required');
                    return;
                  }
                  // Show toast for USP feature
                  showFreeAssessmentToast();
                  setEditOpen(false);
                } catch (e) {
                  toast.error(e?.response?.data?.message || e?.message || 'Failed to update profile');
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password */}
      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {canManage && selectedUser && (selectedUser.id || selectedUser._id) !== (me?.id || me?._id) ? (
              <div>
                <Label>New password</Label>
                <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} placeholder="New#Pass1" />
                {/* Validation badges (same as Create) */}
                {(() => {
                  const v = validatePassword(passwordForm.new_password);
                  const items = [
                    { ok: v.length, label: '16+ chars' },
                    { ok: v.uppercase, label: 'Uppercase' },
                    { ok: v.lowercase, label: 'Lowercase' },
                    { ok: v.special, label: 'Special (!@#$%^&)' },
                    { ok: v.noConsecutive, label: 'No 4+ repeats' },
                  ];
                  return (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {items.map((it, i) => (
                        <span key={i} className={`text-xs px-2 py-1 rounded-full border ${it.ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>
                          {it.label}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <>
                <div>
                  <Label>Current password</Label>
                  <Input type="password" value={ownPasswordForm.current_password} onChange={(e) => setOwnPasswordForm({ ...ownPasswordForm, current_password: e.target.value })} />
                </div>
                <div>
                  <Label>New password</Label>
                  <Input type="password" value={ownPasswordForm.new_password} onChange={(e) => setOwnPasswordForm({ ...ownPasswordForm, new_password: e.target.value })} placeholder="New#Pass1" />
                  {/* Validation badges for own-password change */}
                  {(() => {
                    const v = validatePassword(ownPasswordForm.new_password);
                    const items = [
                      { ok: v.length, label: '16+ chars' },
                      { ok: v.uppercase, label: 'Uppercase' },
                      { ok: v.lowercase, label: 'Lowercase' },
                      { ok: v.special, label: 'Special (!@#$%^&)' },
                      { ok: v.noConsecutive, label: 'No 4+ repeats' },
                    ];
                    return (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {items.map((it, i) => (
                          <span key={i} className={`text-xs px-2 py-1 rounded-full border ${it.ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>
                            {it.label}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            {(() => {
              const isForce = canManage && selectedUser && (selectedUser.id || selectedUser._id) !== (me?.id || me?._id);
              const forceValid = validatePassword(passwordForm.new_password).valid;
              const ownValid = validatePassword(ownPasswordForm.new_password).valid && !!ownPasswordForm.current_password;
              const saveDisabled = submitting || (isForce ? !forceValid : !ownValid);
              const title = saveDisabled ? 'All fields are required and password validations must be satisfied' : '';
              return (
                <span title={title} className={saveDisabled ? 'inline-block cursor-not-allowed' : 'inline-block'}>
                  <Button
                    size="sm"
                    className="bg-[#44087D] hover:bg-[#44087D]/90 text-white"
                    disabled={saveDisabled}
                    onClick={async () => {
                      try {
                        setSubmitting(true);
                        if (isForce) {
                          if (!passwordForm.new_password) {
                            toast.error('New password required');
                            return;
                          }
                          const pv = validatePassword(passwordForm.new_password);
                          if (!pv.valid) {
                            const parts = [];
                            if (!pv.length) parts.push('16+ characters');
                            if (!pv.uppercase) parts.push('an uppercase letter');
                            if (!pv.lowercase) parts.push('a lowercase letter');
                            if (!pv.special) parts.push('a special character (!@#$%^&)');
                            if (!pv.noConsecutive) parts.push('no more than 3 identical characters in a row');
                            toast.error('Password must include: ' + parts.join(', '));
                            return;
                          }
                          // Static: Simulate password change
                          showFreeAssessmentToast();
                        } else {
                          if (!ownPasswordForm.current_password || !ownPasswordForm.new_password) {
                            toast.error('Both passwords required');
                            return;
                          }
                          const pv = validatePassword(ownPasswordForm.new_password);
                          if (!pv.valid) {
                            const parts = [];
                            if (!pv.length) parts.push('16+ characters');
                            if (!pv.uppercase) parts.push('an uppercase letter');
                            if (!pv.lowercase) parts.push('a lowercase letter');
                            if (!pv.special) parts.push('a special character (!@#$%^&)');
                            if (!pv.noConsecutive) parts.push('no more than 3 identical characters in a row');
                            toast.error('Password must include: ' + parts.join(', '));
                            return;
                          }
                          // Static: Simulate password change
                          showFreeAssessmentToast();
                        }
                        setPasswordOpen(false);
                        setPasswordForm({ user_id: '', new_password: '' });
                        setOwnPasswordForm({ current_password: '', new_password: '' });
                      } catch (e) {
                        toast.error(e?.response?.data?.message || e?.message || 'Failed to change password');
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    <KeyRound className="w-4 h-4 mr-1" /> Save
                  </Button>
                </span>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


